import React, { useState, useEffect } from 'react';
import { 
  Stack, 
  Books, 
  Clock, 
  CheckCircle, 
  Circle, 
  Play, 
  Plus, 
  CaretRight, 
  X,
  MagnifyingGlass,
  Folder,
  Compass
} from '@phosphor-icons/react';
import { Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FloatingActionButtons from '../components/FloatingActionButtons';
import LearningStatsHeader from '../components/LearningStatsHeader';
import ManualPathCreator from '../components/ManualPathCreator';
import DiscoverModal from '../components/DiscoverModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { dispatchRateLimitError } from '../utils/api';
import { RateLimitError } from '../types/tier';

interface LearningPath {
  id: string;
  title: string;
  description: string;
  topic: string;
  difficulty_level: string;
  estimated_hours: number;
  status: 'draft' | 'active' | 'completed' | 'archived';
  progress_percentage: number;
  created_at: string;
  stats: {
    total: number;
    completed: number;
    inProgress: number;
    remaining: number;
  };
}

export default function LearningPathsPage() {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [paths, setPaths] = useState<LearningPath[]>([]);
  const [isLoading, setIsLoading] = useState(false); // Start false to prevent flash
  const [initialLoadDone, setInitialLoadDone] = useState(false); // Track first load
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [discoverTopics, setDiscoverTopics] = useState<any[]>([]);
  const [extendSuggestions, setExtendSuggestions] = useState<any[]>([]);
  const [showDiscover, setShowDiscover] = useState(false);
  const [creatingPath, setCreatingPath] = useState<string | null>(null);
  const [highlightedPathId, setHighlightedPathId] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false); // Start false
  const [showManualCreator, setShowManualCreator] = useState(false);
  const [showDiscoverModal, setShowDiscoverModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; path: LearningPath | null }>({
    isOpen: false,
    path: null
  });

  useEffect(() => {
    loadLearningPaths();
    loadDiscoverTopics();
    loadLearningStats();
    
    if (searchParams.get('discover') === 'true') {
      setShowDiscover(true);
    }
    
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightedPathId(highlightId);
      setTimeout(() => {
        setHighlightedPathId(null);
        searchParams.delete('highlight');
        setSearchParams(searchParams, { replace: true });
      }, 3000);
    }
  }, [token, searchParams]);

  useEffect(() => {
    if (highlightedPathId && paths.length > 0) {
      const element = document.getElementById(`path-${highlightedPathId}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [highlightedPathId, paths]);

  const loadLearningPaths = async () => {
    try {
      // Only show loading on first load
      if (!initialLoadDone) {
        setIsLoading(true);
      }
      const response = await fetch('/api/learning-paths', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaths(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load learning paths:', error);
    } finally {
      setIsLoading(false);
      setInitialLoadDone(true);
    }
  };

  const loadDiscoverTopics = async () => {
    try {
      const response = await fetch('/api/learning-paths/discover', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Handle new response format: { topics: [...], extendSuggestions: [...] }
        const responseData = data.data || {};
        const topics = responseData.topics || responseData || []; // Backward compatible
        const extends_ = responseData.extendSuggestions || [];
        
        const topicsWithHours = (Array.isArray(topics) ? topics : []).map((topic: any) => {
          const totalMinutes = (topic.items || []).reduce((sum: number, item: any) => 
            sum + (item.reading_time_minutes || 10), 0
          );
          const estimatedHours = Math.ceil(totalMinutes / 60);
          return { ...topic, estimatedHours };
        });
        setDiscoverTopics(topicsWithHours);
        setExtendSuggestions(extends_);
      }
    } catch (error) {
      console.error('Failed to discover topics:', error);
    }
  };

  const loadLearningStats = async () => {
    try {
      // Only show loading on first load
      if (!initialLoadDone) {
        setStatsLoading(true);
      }
      const response = await fetch('/api/learning-paths/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.data || null);
      }
    } catch (error) {
      console.error('Failed to load learning stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const createLearningPath = async (topic: string, contentIds?: string[]) => {
    try {
      setCreatingPath(topic);
      const response = await fetch('/api/learning-paths/generate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ topic, contentIds })
      });

      if (response.ok) {
        // Refresh both paths AND discover topics to prevent stale suggestions
        await Promise.all([
          loadLearningPaths(),
          loadDiscoverTopics()
        ]);
        toast.success(`Created "${topic}" trail`);
        // Don't close discover panel - let user see updated suggestions
      } else if (response.status === 429) {
        // Rate limit exceeded - dispatch event for upgrade modal
        const errorData = await response.json() as RateLimitError;
        dispatchRateLimitError(errorData);
        toast.error(`Learning path limit reached. ${errorData.usage?.resetIn ? `Resets ${errorData.usage.resetIn}.` : ''}`);
      } else {
        const error = await response.json();
        console.error('Failed to create learning path:', error);
        toast.error(error.message || 'Failed to create trail');
      }
    } catch (error) {
      console.error('Failed to create learning path:', error);
      toast.error('Failed to create trail');
    } finally {
      setCreatingPath(null);
    }
  };

  const [addingToPath, setAddingToPath] = useState<string | null>(null);

  const addItemsToPath = async (pathId: string, contentIds: string[], pathTitle: string) => {
    try {
      setAddingToPath(pathId);
      const response = await fetch(`/api/learning-paths/${pathId}/add-items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contentIds })
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh discover topics to remove the suggestion
        await loadDiscoverTopics();
        // Show success toast
        toast.success(`Added ${data.data.added} item(s) to "${pathTitle}"`);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to add items');
      }
    } catch (error) {
      console.error('Failed to add items to path:', error);
      toast.error('Failed to add items to trail');
    } finally {
      setAddingToPath(null);
    }
  };

  const handleDeletePath = async (path: LearningPath) => {
    try {
      const response = await fetch(`/api/learning-paths/${path.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadLearningPaths();
        setDeleteModal({ isOpen: false, path: null });
      } else {
        const error = await response.json();
        console.error('Failed to delete learning path:', error);
        alert('Failed to delete trail. Please try again.');
      }
    } catch (error) {
      console.error('Failed to delete learning path:', error);
      alert('Failed to delete trail. Please try again.');
    }
  };

  const filteredPaths = paths.filter(path => {
    if (filter === 'all') return path.status !== 'archived';
    if (filter === 'active') return path.status === 'active' || path.status === 'draft';
    if (filter === 'completed') return path.status === 'completed';
    return true;
  });

  const getDifficultyStyle = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/30';
      case 'intermediate': return 'bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/30';
      case 'advanced': return 'bg-violet-50 text-violet-600 border-violet-100 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-800/30';
      default: return 'bg-gray-50 text-gray-600 border-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
    }
  };

  if (isLoading && !initialLoadDone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 font-medium">Loading your trails...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                <Stack weight="duotone" className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">
                  Knowledge Hub
                </h1>
                <p className="text-gray-500 dark:text-gray-400">
                  Curated trails to master your topics
                </p>
              </div>
            </div>
            
            <button
              onClick={() => setShowDiscover(!showDiscover)}
              className="px-5 py-2.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-xl 
                       border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md 
                       transition-all duration-200 font-medium flex items-center gap-2
                       hover:border-blue-200 dark:hover:border-blue-700"
            >
              <Compass weight="duotone" className="w-5 h-5 text-blue-500" />
              <span className="hidden sm:inline">Discover Topics</span>
            </button>
          </div>

          {/* Learning Stats */}
          <LearningStatsHeader stats={stats} isLoading={statsLoading} />

          {/* Filters */}
          <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit">
            {(['all', 'active', 'completed'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  filter === f
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Discover Topics Section */}
        {showDiscover && (
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-8 border border-gray-200 dark:border-gray-700 shadow-sm"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20">
                  <MagnifyingGlass weight="duotone" className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Ready to Create a Trail?
                </h2>
              </div>
              <button
                onClick={() => setShowDiscover(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X weight="bold" className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            {discoverTopics.length > 0 || extendSuggestions.length > 0 ? (
              <>
                {/* Extend Existing Paths Section */}
                {extendSuggestions.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      New content can be added to existing paths:
                    </p>
                    <div className="space-y-2">
                      {extendSuggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 
                                   rounded-lg border border-emerald-100 dark:border-emerald-800/30"
                        >
                          <div className="flex items-center gap-2">
                            <Plus weight="bold" className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {suggestion.count} new {suggestion.count === 1 ? 'item' : 'items'} for 
                              <span className="font-semibold text-emerald-700 dark:text-emerald-400 ml-1">
                                {suggestion.pathTitle}
                              </span>
                            </span>
                          </div>
                          <button
                            onClick={() => addItemsToPath(
                              suggestion.pathId, 
                              suggestion.newItems.map((i: any) => i.id),
                              suggestion.pathTitle
                            )}
                            disabled={addingToPath === suggestion.pathId}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-500 
                                     hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors
                                     flex items-center gap-1.5"
                          >
                            {addingToPath === suggestion.pathId ? (
                              <>
                                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} /></span>
                                <span>Adding...</span>
                              </>
                            ) : (
                              <>
                                <Plus weight="bold" className="w-3.5 h-3.5" />
                                <span>Add to Trail</span>
                              </>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Path Suggestions */}
                {discoverTopics.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {discoverTopics.map((topic, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 dark:bg-gray-900 rounded-xl p-5 border border-gray-100 dark:border-gray-700 
                                 hover:border-blue-200 dark:hover:border-blue-700 transition-all duration-200 memtrail-card"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="font-semibold text-gray-900 dark:text-white flex-1 pr-2">
                            {topic.topic}
                          </h3>
                          {topic.hasPath && (
                            <div className="flex-shrink-0 p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                              <CheckCircle weight="fill" className="w-4 h-4 text-emerald-500" />
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-4 mb-4">
                          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                            <Books weight="duotone" className="w-4 h-4" />
                            <span>{topic.count} items</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                            <Clock weight="duotone" className="w-4 h-4" />
                            <span>~{topic.estimatedHours}h</span>
                          </div>
                        </div>

                        {topic.hasPath ? (
                          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 
                                        rounded-lg border border-emerald-100 dark:border-emerald-800/30">
                            <CheckCircle weight="fill" className="w-4 h-4 text-emerald-500" />
                            <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                              Trail created
                            </span>
                          </div>
                        ) : (
                          <button
                            onClick={() => createLearningPath(topic.topic, topic.items?.map((i: any) => i.id))}
                            disabled={creatingPath === topic.topic}
                            className={`w-full px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 
                                      flex items-center justify-center gap-2 ${
                              creatingPath === topic.topic
                                ? 'bg-blue-500 text-white cursor-wait'
                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                          >
                            {creatingPath === topic.topic ? (
                              <>
                                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 rounded-full bg-white animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} /></span>
                                <span>Creating...</span>
                              </>
                            ) : (
                              <>
                                <Plus weight="bold" className="w-4 h-4" />
                                <span>Create Trail</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 
                              flex items-center justify-center mx-auto mb-4">
                  <Folder weight="duotone" className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No Topics Ready Yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-2 max-w-md mx-auto text-sm">
                  Save at least 3 articles with the same category or tag to unlock trail creation.
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">
                  The more content you save, the better your trails will be!
                </p>
                <button
                  onClick={() => navigate('/library')}
                  className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl 
                           font-medium text-sm transition-all duration-200"
                >
                  Go to Library
                </button>
              </div>
            )}
          </div>
        )}

        {/* Trails Grid */}
        {filteredPaths.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 
                         flex items-center justify-center mx-auto mb-6">
              <Books weight="duotone" className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
              No Trails Yet
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Trails will be automatically suggested when you have 3+ articles on the same topic.
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Save more content to your library to unlock trail suggestions!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {filteredPaths.map((path, idx) => (
              <div
                key={path.id}
                id={`path-${path.id}`}
                onClick={() => navigate(`/learning-paths/${path.id}`)}
                className={`bg-white dark:bg-gray-800 rounded-2xl p-6 border transition-all duration-200 cursor-pointer group ${
                  highlightedPathId === path.id
                    ? 'border-blue-400 dark:border-blue-500 shadow-lg ring-2 ring-blue-100 dark:ring-blue-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-500 transition-colors">
                        {path.title.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </h3>
                      <CaretRight weight="bold" className="w-4 h-4 text-gray-300 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
                    </div>
                    {/* Show actual topics/categories from the trail instead of generic description */}
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {path.stats.total} articles · {path.estimated_hours}h learning path
                    </p>
                  </div>
                  {/* Delete button - visible on hover */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click
                      setDeleteModal({ isOpen: true, path });
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0 ml-2"
                    title="Delete trail"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Meta */}
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${getDifficultyStyle(path.difficulty_level)}`}>
                    {path.difficulty_level}
                  </span>
                  <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-sm">
                    <Clock weight="duotone" className="w-4 h-4" />
                    <span>{path.estimated_hours}h</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-sm">
                    <Books weight="duotone" className="w-4 h-4" />
                    <span>{path.stats.total} items</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500 dark:text-gray-400">Progress</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {path.stats.completed}/{path.stats.total} · {path.progress_percentage}%
                    </span>
                  </div>
                  <div className="h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      style={{ width: `${path.progress_percentage}%` }}
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out"
                    />
                  </div>
                </div>

                {/* Footer Stats - Clear text instead of cryptic icons */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">{path.stats.completed}</span> done
                    </span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-600 dark:text-gray-400">
                      <span className="font-semibold text-gray-900 dark:text-white">{path.stats.remaining}</span> left
                    </span>
                  </div>
                  
                  {path.status === 'completed' && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 
                                  border border-emerald-100 dark:border-emerald-800/30">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Completed</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Floating Action Buttons */}
      <FloatingActionButtons
        showAddItemOnly={true}
        onAddItem={() => setShowManualCreator(true)}
        onDiscover={() => setShowDiscoverModal(true)}
      />
      
      {/* Manual Path Creator Modal */}
      <ManualPathCreator
        isOpen={showManualCreator}
        onClose={() => setShowManualCreator(false)}
        onSuccess={() => {
          loadLearningPaths();
          setShowManualCreator(false);
        }}
      />

      {/* Discover Modal */}
      <DiscoverModal
        isOpen={showDiscoverModal}
        onClose={() => setShowDiscoverModal(false)}
        context="learning-paths"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, path: null })}
        onConfirm={() => deleteModal.path && handleDeletePath(deleteModal.path)}
        title="Delete Trail"
        message={`Are you sure you want to delete "${deleteModal.path?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </div>
  );
}
